/**
 * Блоки «Обзора района» (план v2 §1): графики хода и темпа,
 * «От решения к результату», «Хозяйства: после срока» («Сейчас в районе» —
 * `NowSummary.tsx`). Всё на `ui/*` (`Card`, `DataTable`, `Disclosure`, `Badge`); заголовки
 * графиков — заголовком `Card`, не капслоком `ChartFrame`. Данные приходят
 * готовыми из страницы; расчёты — в `model.ts`.
 *
 * Убрано в v2: тепловая «Ход работ по массивам» и «Очаги» (массив — только
 * фильтр, журнал №85), KPI-плитка на Tailwind (→ `ui/KpiCard`).
 */
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, CalendarClock, CheckCircle2, ClipboardList, Hourglass, PlayCircle, Table2 } from 'lucide-react';
import { Bar, CartesianGrid, ComposedChart, Line, ReferenceLine, Tooltip, XAxis, YAxis } from 'recharts';
import { ChartContainer, ChartLegend, niceTicks, type ChartConfig } from '../../ui/chart/Chart';
import { Card } from '../../ui/Card';
import { DataTable, type Column } from '../../ui/DataTable';
import { Disclosure } from '../../ui/Disclosure';
import { EmptyState } from '../../ui/EmptyState';
import { LoadError } from '../../ui/LoadError';
import { EvidenceMark } from '../../ui/EvidenceMark';
import { Icon } from '../../ui/Icon';
import { SkeletonRegion } from '../../ui/Skeleton';
import { DecisionFlowSkeleton } from './DistrictSkeleton';
import { dateDay, dateShort, num, pct, pctAxis } from '../../lib/format';
import { defineMessages } from '../../../i18n/define';
import { useT } from '../../../i18n/hooks';
import { tr, numberLocale } from '../../../i18n/runtime';

const haWhole = (v: number | null): string => (v === null ? '—' : num(Math.round(v)));
import {
  MIN_AREA_HA,
  type AreaRankedFarm,
  type CampaignPace,
  type SeasonPoint,
} from './model';

const signed = (v: number, digits = 0) => {
  const text = new Intl.NumberFormat(numberLocale(), { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(Math.abs(v));
  return `${v > 0 ? '+' : v < 0 ? '−' : '±'}${text}`;
};

/* ------------------------------------------------------------ KPI */

export type DeltaSense = 'up-good' | 'up-bad';

/** Дельта, раскрашенная по смыслу; `null` — «—», а не ноль. */
export function Delta({ value, unit, sense, digits = 0, suffix }: { value: number | null; unit: string; sense: DeltaSense; digits?: number; suffix: string }) {
  if (value === null) return <span className="district-delta">— {suffix}</span>;
  const rounded = Number(value.toFixed(digits));
  const worse = rounded !== 0 && (sense === 'up-good' ? rounded < 0 : rounded > 0);
  const better = rounded !== 0 && !worse;
  const tone = worse ? 'district-delta district-delta--worse' : better ? 'district-delta district-delta--better' : 'district-delta';
  return <span className={tone}>{signed(rounded, digits)}{unit} {suffix}</span>;
}

/* ------------------------------------------------------------ подсказка графика */

function Tip({ title, rows, note }: { title: string; rows: Array<[string, string, string]>; note?: string | null }) {
  return (
    <div className="achart-tip">
      <div className="achart-tip__title">{title}</div>
      {rows.map(([token, label, text]) => (
        <div className="achart-tip__row" key={label}>
          <i style={{ background: `var(${token})` }} aria-hidden />
          <span>{label}</span>
          <strong>{text}</strong>
        </div>
      ))}
      {note ? <div className="achart-tip__note">{note}</div> : null}
    </div>
  );
}

const TABLE = 'district-table';
const cellCls = '';
const numCls = 'district-table__num';

/* ------------------------------------------------------------ тексты блоков */

const m = defineMessages({
  chartToggleChart: { id: 'district.chart.toggleChart', defaultMessage: 'График', description: 'Кнопка в шапке карточки графика на «Обзоре района»: вернуться от таблицы к графику; коротко' },
  chartToggleTable: { id: 'district.chart.toggleTable', defaultMessage: 'Таблица', description: 'Кнопка в шапке карточки графика на «Обзоре района»: показать те же числа таблицей; коротко' },

  seasonTitle: { id: 'district.season.title', defaultMessage: 'Ход работ сезона', description: 'Заголовок карточки с графиком на «Обзоре района» (хоким, менеджер): накопленная доля выполненного по неделям' },
  seasonWhat: { id: 'district.season.what', defaultMessage: 'ход работ сезона', description: 'Что не загрузилось (винительный падеж, строчными): подставляется в «Не удалось загрузить {what}» в карточке графика' },
  seasonEmpty: { id: 'district.season.empty', defaultMessage: 'Недельного ряда для этого среза нет. Это отсутствие записей, а не нулевой поток отчётов.', description: 'Пустое состояние карточки «Ход работ сезона»: на дату среза нет недельного ряда' },
  seasonChartLabel: { id: 'district.season.chartLabel', defaultMessage: 'Ход работ сезона по неделям', description: 'aria-label графика «Ход работ сезона» для чтеца с экрана' },
  weekCol: { id: 'district.season.col.week', defaultMessage: 'Неделя', description: 'Заголовок первого столбца таблицы «Ход работ сезона»: границы недели' },
  weekRange: { id: 'district.season.weekRange', defaultMessage: '{from} – {to}', description: 'Границы недели в таблице графика; {from}/{to} — короткие даты без года («26 авг – 2 сен»)' },
  weekRangeOpen: { id: 'district.season.weekRangeOpen', defaultMessage: '{from} – {to} (не закрыта)', description: 'Границы недели в таблице графика, когда неделя с датой среза ещё идёт; {from}/{to} — короткие даты без года' },
  weekRangeOpenTip: { id: 'district.season.weekRangeOpenTip', defaultMessage: '{from} – {to} · неделя не закрыта', description: 'Заголовок подсказки над столбиком графика, когда неделя ещё идёт; {from}/{to} — короткие даты без года' },
  weekDoneReports: { id: 'district.season.weekDoneReports', defaultMessage: 'Отчёты «выполнено» за неделю', description: 'Столбец таблицы и строка подсказки графика «Ход работ сезона» (режим по числу работ): отчёты со статусом «выполнено» за неделю' },
  weekDoneArea: { id: 'district.season.weekDoneArea', defaultMessage: 'Га работ, выполненных за неделю', description: 'Столбец таблицы и строка подсказки графика «Ход работ сезона» (режим по площади): гектары работ с первым отчётом «выполнено» за неделю' },
  weekInProgress: { id: 'district.season.weekInProgress', defaultMessage: 'Отчёты «в процессе» за неделю', description: 'Столбец таблицы и строка подсказки графика «Ход работ сезона»: отчёты «в процессе» за неделю' },
  cumulativeDone: { id: 'district.season.cumulativeDone', defaultMessage: 'Выполнено, накопленно', description: 'Столбец таблицы и строка подсказки графика «Ход работ сезона»: накопленная с начала сезона доля выполненного' },
  prevSeasonSeries: { id: 'district.season.prevSeasonSeries', defaultMessage: 'Прошлый сезон', description: 'Имя серии графика по умолчанию: тот же агроцикл прошлого года' },
  deltaVsPrev: { id: 'district.season.deltaVsPrev', defaultMessage: '{delta} п.п. к прошлому сезону', description: 'Строка под подсказкой графика: разница накопленной доли с прошлым сезоном; {delta} — число со знаком («+1,5»)' },
  lastClosedLabel: { id: 'district.season.lastClosedLabel', defaultMessage: '{share} на {date}', description: 'Подпись у вертикальной линии на графике: доля на последнюю закрытую неделю; {share} — «37,5 %», {date} — «26 авг»' },
  legendShareArea: { id: 'district.season.legend.shareArea', defaultMessage: '{cycle}: выполнено, % площади накопленно', description: 'Легенда графика «Ход работ сезона» (режим по площади): линия текущего агроцикла; {cycle} — имя агроцикла («Хлопок 2026»)' },
  legendShareCount: { id: 'district.season.legend.shareCount', defaultMessage: '{cycle}: выполнено, % накопленно', description: 'Легенда графика «Ход работ сезона» (режим по числу работ): линия текущего агроцикла; {cycle} — имя агроцикла' },
  legendPrev: { id: 'district.season.legend.prev', defaultMessage: '{prev} на ту же дату, %', description: 'Легенда графика «Ход работ сезона»: линия прошлого агроцикла на ту же календарную дату; {prev} — имя прошлого агроцикла' },
  legendWeekArea: { id: 'district.season.legend.weekArea', defaultMessage: 'га работ, выполненных за неделю (правая ось)', description: 'Легенда графика «Ход работ сезона»: столбцы, режим по площади; строчными' },
  legendWeekCount: { id: 'district.season.legend.weekCount', defaultMessage: 'отчёты «выполнено» за неделю (правая ось)', description: 'Легенда графика «Ход работ сезона»: столбцы, режим по числу работ; строчными' },
  legendWeekInProgress: { id: 'district.season.legend.weekInProgress', defaultMessage: 'отчёты «в процессе» за неделю', description: 'Легенда графика «Ход работ сезона»: столбцы отчётов «в процессе»; строчными' },
  seasonDenomArea: {
    id: 'district.season.denominator.area',
    defaultMessage: 'линия — % площади работ из {ha} га работ агроцикла {cycle}',
    description: 'Знаменатель справа в заголовке карточки «Ход работ сезона» (режим по площади); {ha} — готовое число гектаров, {cycle} — имя агроцикла',
  },
  seasonDenomCount: {
    id: 'district.season.denominator.count',
    defaultMessage:
      'линия — % выполненных из {count, plural, one {# работы на поле} few {# работ на полях} many {# работ на полях} other {# работы на полях}} агроцикла {cycle}',
    description: 'Знаменатель справа в заголовке карточки «Ход работ сезона» (режим по числу); {count} — число работ на полях агроцикла, {cycle} — имя агроцикла',
  },
  seasonInfoAreaA: {
    id: 'district.season.info.areaA',
    defaultMessage:
      'Линия (левая ось) — доля выполненной площади работ агроцикла к концу недели, % (у каждой работы вес — площадь самой работы на поле (план — площадь размещения, карантин — земля контура, полив — доля посева)). Столбцы (правая ось) — гектары работ, по которым на этой неделе пришёл первый отчёт «выполнено». Неделя, в которую попадает дата среза, ещё не закрыта.',
    description: 'Подсказка «i» карточки «Ход работ сезона» (режим по площади), первый абзац: как читать линию и столбцы',
  },
  seasonInfoAreaB: {
    id: 'district.season.info.areaB',
    defaultMessage:
      'Ряд считает сервер: накопление идёт от итога на дату среза назад, поэтому последняя точка равна главному «Выполнено» ({ha} га работ — знаменатель). Прошлый сезон — прошлый агроцикл той же культуры на ту же дату прошлого года. Отсутствие отчёта не означает, что работа не сделана.',
    description: 'Подсказка «i» карточки «Ход работ сезона» (режим по площади), второй абзац; {ha} — готовое число гектаров работ',
  },
  seasonInfoCountA: {
    id: 'district.season.info.countA',
    defaultMessage:
      'Линия (левая ось) — сколько процентов работ на полях агроцикла выполнено к концу недели. Столбцы (правая ось) — сколько отчётов поступило за эту неделю. Неделя, в которую попадает дата среза, ещё не закрыта.',
    description: 'Подсказка «i» карточки «Ход работ сезона» (режим по числу работ), первый абзац: как читать линию и столбцы',
  },
  seasonInfoCountB: {
    id: 'district.season.info.countB',
    defaultMessage:
      'Выполнено = работы на полях с отчётом «выполнено» / все работы на полях агроцикла на дату среза ({total}). Отчёт «в процессе» — работа начата, но не закончена: в «выполнено» он не входит. Накопление идёт от итога на дату среза назад по датам выполнения в отчётах. Столбцы — отчёты недели по дате выполнения (одна работа на поле может дать два отчёта: «в процессе», потом «выполнено»). Прошлый сезон — прошлый агроцикл той же культуры на ту же дату прошлого года. Отсутствие отчёта не означает, что работа не сделана.',
    description: 'Подсказка «i» карточки «Ход работ сезона» (режим по числу работ), второй абзац; {total} — готовое число работ на полях',
  },

  paceTitle: { id: 'district.pace.title', defaultMessage: 'Темп кампании · {title}', description: 'Заголовок карточки графика «Темп кампании» на «Обзоре района»; {title} — название работы кампании («Культивация»)' },
  paceEmpty: { id: 'district.pace.empty', defaultMessage: 'Дневного ряда отчётов для кампании нет — темп не посчитан.', description: 'Пустое состояние карточки «Темп кампании»: у кампании нет дневного ряда отчётов' },
  paceChartLabel: { id: 'district.pace.chartLabel', defaultMessage: 'Темп текущей кампании по дням', description: 'aria-label графика «Темп кампании» для чтеца с экрана' },
  paceDayCol: { id: 'district.pace.col.day', defaultMessage: 'День', description: 'Заголовок первого столбца таблицы «Темп кампании»: дата' },
  paceDoneDay: { id: 'district.pace.doneDay', defaultMessage: 'Выполнено за день', description: 'Столбец таблицы и строка подсказки графика «Темп кампании» (режим по числу работ): сколько выполнено за день' },
  paceDoneDayArea: { id: 'district.pace.doneDayArea', defaultMessage: 'Выполнено за день, га работ', description: 'Столбец таблицы и строка подсказки графика «Темп кампании» (режим по площади): гектары работ, выполненные за день' },
  paceCumulative: { id: 'district.pace.cumulative', defaultMessage: 'Выполнено, накопленно', description: 'Столбец таблицы и строка подсказки графика «Темп кампании» (режим по числу работ): выполнено нарастающим итогом' },
  paceCumulativeArea: { id: 'district.pace.cumulativeArea', defaultMessage: 'Выполнено, накопленно, га работ', description: 'Столбец таблицы и строка подсказки графика «Темп кампании» (режим по площади): гектары работ нарастающим итогом' },
  paceInProgress: { id: 'district.pace.inProgress', defaultMessage: 'В процессе', description: 'Столбец таблицы и строка подсказки графика «Темп кампании»: работы с отчётом «в процессе»' },
  paceNeed: { id: 'district.pace.need', defaultMessage: 'Нужно', description: 'Столбец таблицы и строка подсказки графика «Темп кампании»: сколько нужно выполнить к этому дню для равного темпа до конца окна' },
  paceSeriesDone: { id: 'district.pace.series.done', defaultMessage: 'Выполнено за день', description: 'Имя серии графика «Темп кампании» (столбцы) в конфигурации графика' },
  paceSeriesNeed: { id: 'district.pace.series.need', defaultMessage: 'Нужно, чтобы успеть', description: 'Имя серии графика «Темп кампании» (пунктирная линия) в конфигурации графика' },
  paceSeriesInProgress: { id: 'district.pace.series.inProgress', defaultMessage: 'В процессе на этот день', description: 'Имя серии графика «Темп кампании» (линия «в процессе») в конфигурации графика' },
  paceLegendCumulative: { id: 'district.pace.legend.cumulative', defaultMessage: 'выполнено, накопленно', description: 'Легенда графика «Темп кампании»: линия нарастающего итога; строчными' },
  paceLegendInProgress: { id: 'district.pace.legend.inProgress', defaultMessage: 'в процессе на этот день', description: 'Легенда графика «Темп кампании»: линия работ «в процессе»; строчными' },
  paceLegendNeed: { id: 'district.pace.legend.need', defaultMessage: 'нужно к {date}', description: 'Легенда графика «Темп кампании»: пунктир нужного темпа до конца окна; {date} — дата конца окна без года («15 мая»)' },
  paceLegendDone: { id: 'district.pace.legend.done', defaultMessage: 'выполнено за день (правая ось)', description: 'Легенда графика «Темп кампании»: столбцы выполненного за день; строчными' },
  paceDenomArea: { id: 'district.pace.denominator.area', defaultMessage: 'из {ha} га работ кампании', description: 'Знаменатель справа в заголовке карточки «Темп кампании» (режим по площади); {ha} — готовое число гектаров работ' },
  paceDenomCount: {
    id: 'district.pace.denominator.count',
    defaultMessage: 'из {count, plural, one {# работы на поле} few {# работ на полях} many {# работ на полях} other {# работы на полях}} кампании',
    description: 'Знаменатель справа в заголовке карточки «Темп кампании» (режим по числу); {count} — число работ на полях кампании',
  },
  paceInfoAreaA: { id: 'district.pace.info.areaA', defaultMessage: 'Линия «нужно» — равный темп до конца окна, чтобы выполнить всю площадь работ кампании.', description: 'Подсказка «i» карточки «Темп кампании» (режим по площади), первый абзац' },
  paceInfoAreaB: {
    id: 'district.pace.info.areaB',
    defaultMessage:
      'Столбцы — гектары работ, по которым в этот день пришёл первый отчёт «выполнено» (вес работы — площадь самой работы на поле: план — площадь размещения, карантин — земля контура, полив — доля посева). «Выполнено, накопленно» — га работ к этому дню: сервер считает от итога кампании на дату среза назад, поэтому последняя точка равна «выполнено» кампании. «Нужно» — прямая от выполненного на дату среза до всей площади работ к последнему дню окна. Темп 7 дней — среднее выполненных га за последние семь дней ряда.',
    description: 'Подсказка «i» карточки «Темп кампании» (режим по площади), второй абзац',
  },
  paceInfoCountA: { id: 'district.pace.info.countA', defaultMessage: 'Линия «нужно» — равный темп до конца окна, чтобы выполнить все работы на полях кампании.', description: 'Подсказка «i» карточки «Темп кампании» (режим по числу работ), первый абзац' },
  paceInfoCountB: {
    id: 'district.pace.info.countB',
    defaultMessage:
      'Столбцы — работы на полях, по которым в этот день пришёл первый отчёт «выполнено» (дата выполнения в отчёте). «Выполнено, накопленно» — их сумма к этому дню. «В процессе» — работы на полях с отчётом «в процессе», но ещё без «выполнено». «Нужно» — прямая от выполненного на дату среза до всех работ на полях кампании к последнему дню окна. Темп 7 дней — среднее выполненного за последние семь дней ряда.',
    description: 'Подсказка «i» карточки «Темп кампании» (режим по числу работ), второй абзац',
  },

  flowTitle: { id: 'district.flow.title', defaultMessage: 'От решения к результату', description: 'Заголовок карточки на «Обзоре района» (хоким, менеджер): шаги поручений от назначения до проверки результата' },
  flowAll: { id: 'district.flow.all', defaultMessage: 'Все поручения →', description: 'Ссылка в шапке карточки «От решения к результату» на список поручений; со стрелкой, коротко' },
  flowInfo: {
    id: 'district.flow.info',
    defaultMessage: '«Отчитались по поручению» не означает «работа подтверждена»: вернитесь к тем же данным и сравните показатели до и после. Шаг — статус поручения; нажатие открывает «Поручения» с этим фильтром.',
    description: 'Подсказка «i» карточки «От решения к результату»: как читать шаги поручений',
  },
  flowWhat: { id: 'district.flow.what', defaultMessage: 'поручения', description: 'Что не загрузилось (винительный падеж, строчными): «Не удалось загрузить поручения»; также подпись заглушки загрузки' },
  flowStep: { id: 'district.flow.step', defaultMessage: '{n}. {label}', description: 'Название шага поручений с порядковым номером: «1. Назначено»; {n} — номер шага' },
  flowOpen: { id: 'district.flow.open.label', defaultMessage: 'Назначено', description: 'Название шага поручений (карточка «От решения к результату»): поручение дано, ещё никто не взял; коротко' },
  flowOpenNote: { id: 'district.flow.open.note', defaultMessage: 'передано, ещё не взяли', description: 'Подсказка под числом шага «Назначено»: поручение передано, но ответственный ещё не взял его' },
  flowInProgress: { id: 'district.flow.inProgress.label', defaultMessage: 'В работе', description: 'Название шага поручений (карточка «От решения к результату»): ответственный взял поручение; коротко' },
  flowInProgressNote: { id: 'district.flow.inProgress.note', defaultMessage: 'ответственный взял', description: 'Подсказка под числом шага «В работе»' },
  flowDone: { id: 'district.flow.done.label', defaultMessage: 'Ждёт результата', description: 'Название шага поручений (карточка «От решения к результату»): по поручению отчитались, результат не проверен; коротко' },
  flowDoneNote: { id: 'district.flow.done.note', defaultMessage: 'отчитались — сверить данные', description: 'Подсказка под числом шага «Ждёт результата»' },
  flowChecked: { id: 'district.flow.checked.label', defaultMessage: 'Закрыто', description: 'Название шага поручений (карточка «От решения к результату»): результат проверен; коротко' },
  flowCheckedNote: { id: 'district.flow.checked.note', defaultMessage: 'результат проверен', description: 'Подсказка под числом шага «Закрыто»' },
  flowLate: {
    id: 'district.flow.late',
    defaultMessage: '{count, plural, one {# поручение после срока} few {# поручения после срока} many {# поручений после срока} other {# поручения после срока}}',
    description: 'Строка с предупреждением под шагами поручений: сколько открытых поручений просрочено',
  },

  lateTitle: { id: 'district.late.title', defaultMessage: 'Хозяйства: после срока', description: 'Заголовок свёрнутого блока внизу «Обзора района» (хоким, менеджер): хозяйства, у которых больше всего земли с работами после срока' },
  lateColFarm: { id: 'district.late.col.farm', defaultMessage: 'Хозяйство', description: 'Заголовок колонки таблицы «Хозяйства: после срока»: название хозяйства' },
  lateColOverdue: { id: 'district.late.col.overdue', defaultMessage: 'После срока', description: 'Заголовок колонки таблицы «Хозяйства: после срока»: земля, где есть работа после срока без отчёта; над единицей' },
  lateUnitLand: { id: 'district.late.unit.landHa', defaultMessage: 'га земли', description: 'Единица под заголовком колонки: гектары земли (каждый гектар один раз); узкая колонка' },
  lateColLand: { id: 'district.late.col.land', defaultMessage: 'Земля в цикле', description: 'Заголовок колонки таблицы «Хозяйства: после срока»: земля хозяйства под работами агроцикла' },
  lateUnitHa: { id: 'district.late.unit.ha', defaultMessage: 'га', description: 'Единица под заголовком колонки: гектары земли; узкая колонка' },
  lateColShare: { id: 'district.late.col.share', defaultMessage: 'Доля', description: 'Заголовок колонки таблицы «Хозяйства: после срока»: доля земли после срока от земли хозяйства' },
  lateUnitShare: { id: 'district.late.unit.share', defaultMessage: '% земли', description: 'Единица под заголовком колонки: процент земли хозяйства; узкая колонка' },
  lateColWorks: { id: 'district.late.col.works', defaultMessage: 'Работ после срока', description: 'Заголовок колонки таблицы «Хозяйства: после срока»: число работ на полях после срока' },
  lateWorksCell: { id: 'district.late.cell.works', defaultMessage: '{overdue} из {total}', description: 'Ячейка колонки «Работ после срока»: {overdue} из {total} — числа работ на полях' },
  lateWorksCellHa: { id: 'district.late.cell.worksHa', defaultMessage: '{overdue} из {total} · {ha} га работ', description: 'Ячейка колонки «Работ после срока» с гектарами работ; {overdue}/{total} — числа работ на полях, {ha} — сумма гектаров этих работ' },
  lateTop3Item: { id: 'district.late.top3Item', defaultMessage: '{name} {ha} га', description: 'Хозяйство в краткой подписи свёрнутого блока: «Бобур 120 га»; {name} — имя хозяйства, {ha} — гектары земли после срока' },
  lateSummary: { id: 'district.late.summary', defaultMessage: 'больше всего: {top}', description: 'Краткая подпись свёрнутого блока «Хозяйства: после срока»; {top} — три хозяйства через « · »' },
  lateSummaryNone: { id: 'district.late.summaryNone', defaultMessage: 'хозяйств с землёй ≥{min} га и указанной площадью нет', description: 'Краткая подпись свёрнутого блока, когда ранжировать некого; {min} — порог земли в гектарах' },
  lateDenominator: {
    id: 'district.late.denominator',
    defaultMessage: '{count, plural, one {хозяйство} few {хозяйства} many {хозяйств} other {хозяйства}}',
    description: 'Слово-знаменатель под таблицей «Хозяйства: после срока»: «10 хозяйств»; без числа, склоняется по {count}',
  },
  lateAll: { id: 'district.late.all', defaultMessage: 'Все хозяйства', description: 'Ссылка под таблицей «Хозяйства: после срока» на реестр хозяйств; коротко' },
  lateEmptyTitle: { id: 'district.late.empty.title', defaultMessage: 'Ранжировать некого', description: 'Заголовок пустого состояния таблицы «Хозяйства: после срока»' },
  lateEmptyReason: { id: 'district.late.empty.reason', defaultMessage: 'в срезе нет хозяйств с землёй ≥{min} га под работами агроцикла и указанной площадью работ', description: 'Пояснение в пустом состоянии таблицы «Хозяйства: после срока»; строчными; {min} — порог земли в гектарах' },
  lateCaption: {
    id: 'district.late.caption',
    defaultMessage:
      'Порядок — по земле, где есть работа после срока без отчёта (каждый гектар один раз): наверху те, у кого такой земли больше, а не мелкие хозяйства с высокой долей и не те, у кого просто больше работ. Доля = эта земля / земля хозяйства под работами агроцикла. Мелко — число работ на полях после срока и их гектары работ: сумма площадей работ, где поле с тремя работами после срока считается трижды, поэтому она бывает больше земли. В списке {count, plural, one {# хозяйство} few {# хозяйства} many {# хозяйств} other {# хозяйства}} с землёй ≥{min} га. Это очередь уточнения, а не рейтинг: нет отчёта ≠ не сделано.',
    description: 'Пояснение под таблицей «Хозяйства: после срока»: как устроен порядок и доля; {count} — сколько хозяйств в списке, {min} — порог земли в гектарах',
  },

  emptyData: { id: 'district.empty.title', defaultMessage: 'Нет данных', description: 'Заголовок пустого состояния карточки блока «Обзора района» (под ним — причина)' },
});

/* ------------------------------------------------------------ рамка графика */

/** Рамка графика на `ui/Card`: заголовок карточки, знаменатель, демо, таблица по кнопке. */
function ChartCard({ title, denominator, demo, table, info, children }: {
  title: string;
  denominator: string;
  demo: boolean;
  table: ReactNode;
  /** Как читать график — значком «i» у заголовка (07.10.2026), не абзацем под графиком. */
  info: ReactNode;
  children: ReactNode;
}) {
  const t = useT();
  const [asTable, setAsTable] = useState(false);
  return (
    <Card
      className="district-chart"
      title={title}
      info={info}
      aside={<span className="district-chart__aside">{denominator}{demo ? <EvidenceMark kind="demo" /> : null}
        <button type="button" className="district-chart__toggle district-noprint" aria-pressed={asTable} onClick={() => setAsTable(v => !v)}><Icon icon={Table2} size={16} />{asTable ? t(m.chartToggleChart) : t(m.chartToggleTable)}</button></span>}
    >
      {asTable ? <div className="district-chart__table">{table}</div> : children}
    </Card>
  );
}

/* ------------------------------------------------------------ ход работ сезона */

/** Серии графика: подписи — каталог, строятся при рендере (язык может смениться). */
function seasonConfig(): ChartConfig {
  return {
    // i18n-ignore: формы нужны типу серии, но подсказка графика собрана вручную (Tip) и числа со словом не печатает
    weekDone: { label: tr(m.weekDoneReports), token: '--data-ok-soft', forms: ['отчёт', 'отчёта', 'отчётов'] },
    // i18n-ignore: см. выше — формы в этом графике не выводятся
    weekInProgress: { label: tr(m.weekInProgress), token: '--data-ok-started', forms: ['отчёт', 'отчёта', 'отчётов'] },
    // i18n-ignore: знак процента, не слово
    share: { label: tr(m.cumulativeDone), token: '--data-ok', forms: ['%', '%', '%'] },
    // i18n-ignore: знак процента, не слово
    prevShare: { label: tr(m.prevSeasonSeries), token: '--data-missing', forms: ['%', '%', '%'] },
  };
}

/**
 * «Ход работ сезона» (agroai-6al.35): линия — накопленная доля ВЫПОЛНЕННЫХ
 * обязательств цикла (отчёт «выполнено»), столбцы — отчёты, поступившие за
 * неделю, с разрезом «выполнено / в процессе». Подписи называют, что
 * накапливается и за какой период, — без безымянных показателей.
 */
export function SeasonChart({ points, cycleName, prevName, total, areaHa = null, demo, print, error, onRetry }: {
  points: SeasonPoint[];
  cycleName: string;
  prevName: string | null;
  total: number;
  /** Ряд в гектарах работ (сервер, G1): знаменатель — га работ цикла; `null` — ряд по счёту. */
  areaHa?: number | null;
  demo: boolean;
  print: boolean;
  /** Сводка не загрузилась — ошибка с повтором, а не «ряда нет». */
  error?: { message?: string } | null;
  onRetry?: () => void;
}) {
  const t = useT();
  if (error) return <Card title={t(m.seasonTitle)}><LoadError compact what={t(m.seasonWhat)} error={error} onRetry={onRetry} /></Card>;
  if (!points.length) return <EmptyBlock title={t(m.seasonTitle)} text={t(m.seasonEmpty)} />;
  const config = seasonConfig();
  const hasPrev = points.some(p => p.prevShare !== null);
  const split = points.some(p => p.weekInProgress !== null);
  const lastClosed = [...points].reverse().find(p => !p.open);
  const barTicks = niceTicks(Math.max(1, ...points.map(p => (p.weekDone ?? 0) + (p.weekInProgress ?? 0))));
  const label = (d: unknown) => dateDay(String(d));
  const area = areaHa !== null;
  const weekWord = area ? t(m.weekDoneArea) : t(m.weekDoneReports);
  const weekNum = (v: number | null) => (v === null ? '—' : num(area ? Math.round(v) : v));
  const table = (
    <table className={TABLE}>
      <thead><tr><th className={cellCls}>{t(m.weekCol)}</th><th className={numCls}>{weekWord}</th>{split ? <th className={numCls}>{t(m.weekInProgress)}</th> : null}<th className={numCls}>{t(m.cumulativeDone)}</th>{hasPrev ? <th className={numCls}>{prevName}</th> : null}</tr></thead>
      <tbody>{points.map(p => <tr key={p.week}><td className={cellCls}>{t(p.open ? m.weekRangeOpen : m.weekRange, { from: dateDay(p.weekFrom), to: dateDay(p.week) })}</td><td className={numCls}>{weekNum(p.weekDone)}</td>{split ? <td className={numCls}>{num(p.weekInProgress)}</td> : null}<td className={numCls}>{pctAxis(p.share)}</td>{hasPrev ? <td className={numCls}>{pctAxis(p.prevShare)}</td> : null}</tr>)}</tbody>
    </table>
  );
  return (
    <ChartCard
      title={t(m.seasonTitle)}
      denominator={area ? t(m.seasonDenomArea, { ha: num(Math.round(areaHa!)), cycle: cycleName }) : t(m.seasonDenomCount, { count: total, cycle: cycleName })}
      demo={demo}
      table={table}
      info={area ? <><p>{t(m.seasonInfoAreaA)}</p><p>{t(m.seasonInfoAreaB, { ha: num(Math.round(areaHa!)) })}</p></> : <><p>{t(m.seasonInfoCountA)}</p><p>{t(m.seasonInfoCountB, { total: num(total) })}</p></>}
    >
      <ChartContainer config={config} height={print ? 220 : 280} label={t(m.seasonChartLabel)}>
        <ComposedChart data={points} margin={{ top: 18, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid vertical={false} strokeDasharray="3 4" />
          <XAxis dataKey="week" tickLine={false} axisLine={false} tickMargin={8} minTickGap={12} tickFormatter={label} />
          <YAxis yAxisId="share" width={44} domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} tickLine={false} axisLine={false} tickFormatter={(v: number) => `${v}%`} />
          <YAxis yAxisId="week" orientation="right" width={48} ticks={barTicks} domain={[0, barTicks.at(-1)!]} tickLine={false} axisLine={false} tickFormatter={(v: number) => num(v)} />
          {!print ? <Tooltip content={({ active, payload, label: l }) => {
            if (!active || !payload?.length) return null;
            const p = points.find(x => x.week === l);
            if (!p) return null;
            const d = p.share !== null && p.prevShare !== null ? p.share - p.prevShare : null;
            const range = { from: dateDay(p.weekFrom), to: dateDay(p.week) };
            return <Tip
              title={p.open ? t(m.weekRangeOpenTip, range) : t(m.weekRange, range)}
              rows={[
                ['--data-ok', t(m.cumulativeDone), pctAxis(p.share)],
                ['--data-ok-soft', weekWord, weekNum(p.weekDone)],
                ...(split ? [['--data-ok-started', t(m.weekInProgress), num(p.weekInProgress)] as [string, string, string]] : []),
                ...(hasPrev ? [['--data-missing', prevName ?? '', pctAxis(p.prevShare)] as [string, string, string]] : []),
              ]}
              note={d === null ? null : t(m.deltaVsPrev, { delta: signed(d, 1) })}
            />;
          }} /> : null}
          <Bar yAxisId="week" dataKey="weekDone" stackId="week" fill="var(--color-weekDone)" isAnimationActive={false} />
          {split ? <Bar yAxisId="week" dataKey="weekInProgress" stackId="week" fill="var(--color-weekInProgress)" radius={[3, 3, 0, 0]} isAnimationActive={false} /> : null}
          {hasPrev ? <Line yAxisId="share" dataKey="prevShare" stroke="var(--color-prevShare)" strokeWidth={2} strokeDasharray="5 4" dot={false} isAnimationActive={false} connectNulls /> : null}
          <Line yAxisId="share" dataKey="share" stroke="var(--color-share)" strokeWidth={2.5} dot={false} isAnimationActive={false} />
          {lastClosed ? <ReferenceLine yAxisId="share" x={lastClosed.week} stroke="var(--data-axis)" strokeDasharray="2 3" label={{ value: t(m.lastClosedLabel, { share: pctAxis(lastClosed.share), date: dateDay(lastClosed.week) }), position: 'insideTopRight', fontSize: 11, fill: 'var(--ink)' }} /> : null}
        </ComposedChart>
      </ChartContainer>
      <ChartLegend config={{
        share: { ...config.share!, label: t(area ? m.legendShareArea : m.legendShareCount, { cycle: cycleName }) },
        ...(hasPrev ? { prevShare: { ...config.prevShare!, label: t(m.legendPrev, { prev: prevName ?? '' }) } } : {}),
        weekDone: { ...config.weekDone!, label: t(area ? m.legendWeekArea : m.legendWeekCount) },
        ...(split ? { weekInProgress: { ...config.weekInProgress!, label: t(m.legendWeekInProgress) } } : {}),
      }} />
    </ChartCard>
  );
}

/* ------------------------------------------------------------ темп кампании */

function paceConfig(): ChartConfig {
  return {
    // i18n-ignore: формы нужны типу серии; подсказка графика собрана вручную (Tip)
    done: { label: tr(m.paceSeriesDone), token: '--data-ok-soft', forms: ['обязательство', 'обязательства', 'обязательств'] },
    // i18n-ignore: см. выше
    cumulative: { label: tr(m.paceCumulative), token: '--data-ok', forms: ['обязательство', 'обязательства', 'обязательств'] },
    // i18n-ignore: см. выше
    inProgress: { label: tr(m.paceSeriesInProgress), token: '--data-ok-started', forms: ['обязательство', 'обязательства', 'обязательств'] },
    // i18n-ignore: см. выше
    need: { label: tr(m.paceSeriesNeed), token: '--data-warning', forms: ['обязательство', 'обязательства', 'обязательств'] },
  };
}

export function PaceChart({ title, heading, pace, total, windowTo, demo, print }: {
  title: string;
  /** Заголовок карточки вместо «Темп кампании · …» (страница работы). */
  heading?: string;
  pace: CampaignPace;
  total: number;
  windowTo: string;
  demo: boolean;
  print: boolean;
}) {
  const t = useT();
  const cardTitle = heading ?? t(m.paceTitle, { title });
  if (!pace.series.length) return <EmptyBlock title={cardTitle} text={t(m.paceEmpty)} />;
  const config = paceConfig();
  const split = pace.series.some(p => p.inProgress !== null);
  // Гектары работ (G1, бывш. 1ed): ряд и итог с сервера; без площади — работы.
  const areaMode = pace.unit === 'ha';
  const fmt = (v: number | null) => (v === null ? '—' : areaMode ? num(Math.round(v)) : num(v));
  const doneDayLabel = areaMode ? t(m.paceDoneDayArea) : t(m.paceDoneDay);
  const cumulativeLabel = areaMode ? t(m.paceCumulativeArea) : t(m.paceCumulative);
  const cumTicks = niceTicks(Math.max(1, areaMode ? pace.total ?? total : total));
  const dayTicks = niceTicks(Math.max(1, ...pace.series.map(p => p.done ?? 0)));
  const table = (
    <table className={TABLE}>
      <thead><tr><th className={cellCls}>{t(m.paceDayCol)}</th><th className={numCls}>{doneDayLabel}</th><th className={numCls}>{cumulativeLabel}</th>{split ? <th className={numCls}>{t(m.paceInProgress)}</th> : null}<th className={numCls}>{t(m.paceNeed)}</th></tr></thead>
      <tbody>{pace.series.map(p => <tr key={p.day}><td className={cellCls}>{dateDay(p.day)}</td><td className={numCls}>{fmt(p.done)}</td><td className={numCls}>{fmt(p.cumulative)}</td>{split ? <td className={numCls}>{num(p.inProgress)}</td> : null}<td className={numCls}>{p.need === null ? '—' : num(Math.round(p.need))}</td></tr>)}</tbody>
    </table>
  );
  return (
    <ChartCard
      title={cardTitle}
      denominator={areaMode ? t(m.paceDenomArea, { ha: num(Math.round(pace.total ?? 0)) }) : t(m.paceDenomCount, { count: total })}
      demo={demo}
      table={table}
      info={areaMode
        ? <><p>{t(m.paceInfoAreaA)}</p><p>{t(m.paceInfoAreaB)}</p></>
        : <><p>{t(m.paceInfoCountA)}</p><p>{t(m.paceInfoCountB)}</p></>}
    >
      <ChartContainer config={config} height={print ? 220 : 280} label={t(m.paceChartLabel)}>
        <ComposedChart data={pace.series} margin={{ top: 12, right: 4, bottom: 0, left: 0 }}>
          <CartesianGrid vertical={false} strokeDasharray="3 4" />
          <XAxis dataKey="day" tickLine={false} axisLine={false} tickMargin={8} minTickGap={14} tickFormatter={(d: string) => dateDay(d)} />
          <YAxis yAxisId="cum" width={48} ticks={cumTicks} domain={[0, cumTicks.at(-1)!]} tickLine={false} axisLine={false} tickFormatter={(v: number) => num(v)} />
          <YAxis yAxisId="day" orientation="right" width={areaMode ? 48 : 36} ticks={dayTicks} domain={[0, dayTicks.at(-1)!]} tickLine={false} axisLine={false} tickFormatter={(v: number) => num(v)} />
          {!print ? <Tooltip content={({ active, label: l }) => {
            if (!active) return null;
            const p = pace.series.find(x => x.day === l);
            if (!p) return null;
            return <Tip title={dateShort(p.day)} rows={[
              ['--data-ok-soft', doneDayLabel, fmt(p.done)],
              ['--data-ok', cumulativeLabel, fmt(p.cumulative)],
              ...(split ? [['--data-ok-started', t(m.paceInProgress), num(p.inProgress)] as [string, string, string]] : []),
              ['--data-warning', t(m.paceNeed), p.need === null ? '—' : num(Math.round(p.need))],
            ]} />;
          }} /> : null}
          <Bar yAxisId="day" dataKey="done" fill="var(--color-done)" radius={[2, 2, 0, 0]} isAnimationActive={false} />
          <Line yAxisId="cum" dataKey="cumulative" stroke="var(--color-cumulative)" strokeWidth={2.5} dot={false} isAnimationActive={false} />
          {split ? <Line yAxisId="cum" dataKey="inProgress" stroke="var(--color-inProgress)" strokeWidth={2} dot={false} isAnimationActive={false} /> : null}
          <Line yAxisId="cum" dataKey="need" stroke="var(--color-need)" strokeWidth={2} strokeDasharray="5 4" dot={false} isAnimationActive={false} connectNulls />
        </ComposedChart>
      </ChartContainer>
      <ChartLegend config={{
        cumulative: { ...config.cumulative!, label: t(m.paceLegendCumulative) },
        ...(split ? { inProgress: { ...config.inProgress!, label: t(m.paceLegendInProgress) } } : {}),
        need: { ...config.need!, label: t(m.paceLegendNeed, { date: dateDay(windowTo) }) },
        done: { ...config.done!, label: t(m.paceLegendDone) },
      }} />
    </ChartCard>
  );
}

/* ------------------------------------------------------------ от решения к результату */

const FLOW = [
  { id: 'open', label: m.flowOpen, note: m.flowOpenNote, icon: ClipboardList },
  { id: 'in_progress', label: m.flowInProgress, note: m.flowInProgressNote, icon: PlayCircle },
  { id: 'done', label: m.flowDone, note: m.flowDoneNote, icon: Hourglass },
  { id: 'checked', label: m.flowChecked, note: m.flowCheckedNote, icon: CheckCircle2 },
] as const;

/** «От решения к результату» (из K2 `ControlPanel`): шаги поручений с числами. */
export function DecisionFlow({ counts, hrefFor, allHref, late, error, onRetry }: {
  counts: Record<string, number> | null;
  hrefFor: (status: string) => string;
  allHref: string;
  late: number;
  /** Список поручений не загрузился — ошибка с повтором. */
  error?: { message?: string } | null;
  onRetry?: () => void;
}) {
  const t = useT();
  return (
    <Card
      title={t(m.flowTitle)}
      aside={<Link to={allHref}>{t(m.flowAll)}</Link>}
      info={t(m.flowInfo)}
    >
      {counts === null ? (error ? <LoadError compact what={t(m.flowWhat)} error={error} onRetry={onRetry} /> : <SkeletonRegion label={t(m.flowWhat)}><DecisionFlowSkeleton /></SkeletonRegion>) : (
        <>
          <ol className="district-flow">
            {FLOW.map((s, i) => (
              <li key={s.id}>
                <Link to={hrefFor(s.id)} className="district-flow__step linkcard">
                  <span className="district-flow__label"><Icon icon={s.icon} size={16} />{t(m.flowStep, { n: i + 1, label: t(s.label) })}</span>
                  <strong className="district-flow__num">{num(counts[s.id] ?? 0)}</strong>
                  <span className="district-flow__hint">{t(s.note)}</span>
                  <span className="linkcard__go" aria-hidden="true"><Icon icon={ArrowUpRight} size={16} /></span>
                </Link>
              </li>
            ))}
          </ol>
          {late ? (
            <p className="district-flow__late-row">
              <Icon icon={CalendarClock} size={16} />
              <span className="district-flow__late">{t(m.flowLate, { count: late })}</span>
            </p>
          ) : null}
        </>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------ хозяйства после срока */

/**
 * «Хозяйства: после срока» — топ-10 по ГЕКТАРАМ работ после срока без отчёта
 * (`rankFarmsByArea`, решение №645, аудит KPI 3.5 «где ущерб»): доля площади
 * и число работ рядом. Свёрнуто; в заголовке — топ-3.
 */
export function LateFarms({ ranked, eligible, farmHref, allHref }: {
  ranked: AreaRankedFarm[];
  eligible: number;
  farmHref: (id: number) => string;
  allHref: string;
}) {
  const t = useT();
  const columns: Column<AreaRankedFarm>[] = [
    { key: 'name', header: t(m.lateColFarm), render: f => <Link to={farmHref(f.id)}>{f.name}</Link> },
    { key: 'overdueHa', header: t(m.lateColOverdue), unit: t(m.lateUnitLand), numeric: true, render: f => haWhole(f.overdueHa) },
    { key: 'area', header: t(m.lateColLand), unit: t(m.lateUnitHa), numeric: true, render: f => haWhole(f.areaHa) },
    { key: 'overduePct', header: t(m.lateColShare), unit: t(m.lateUnitShare), numeric: true, render: f => pct(f.overduePct, 1) },
    { key: 'overdue', header: t(m.lateColWorks), numeric: true, render: f => f.overdueWorkHa !== null ? t(m.lateWorksCellHa, { overdue: num(f.overdue), total: num(f.total), ha: haWhole(f.overdueWorkHa) }) : t(m.lateWorksCell, { overdue: num(f.overdue), total: num(f.total) }) },
  ];
  const top3 = ranked.slice(0, 3).map(f => t(m.lateTop3Item, { name: f.name, ha: haWhole(f.overdueHa) })).join(' · ');
  return (
    <Disclosure
      label={t(m.lateTitle)}
      summary={ranked.length ? t(m.lateSummary, { top: top3 }) : t(m.lateSummaryNone, { min: MIN_AREA_HA })}
    >
      <DataTable
        columns={columns}
        rows={ranked}
        getKey={f => String(f.id)}
        limit={10}
        denominator={m.lateDenominator}
        more={{ to: allHref, label: t(m.lateAll) }}
        empty={{ title: t(m.lateEmptyTitle), reason: t(m.lateEmptyReason, { min: MIN_AREA_HA }) }}
      />
      <p className="district-chart__caption">{t(m.lateCaption, { count: eligible, min: MIN_AREA_HA })}</p>
    </Disclosure>
  );
}

/* ------------------------------------------------------------ пусто */

export function EmptyBlock({ title, text }: { title: string; text: string }) {
  const t = useT();
  return (
    <Card title={title}>
      <EmptyState compact title={t(m.emptyData)} reason={text} />
    </Card>
  );
}
